const { getElement, debounce } = window.BAO.Utils

function isCartPage (pathname = window.location.pathname) {
  const { rootUrl } = window.routes
  const marketPattern = new RegExp(`^${rootUrl}`)
  const pathnameWithoutMarket =
    rootUrl === '/' ? pathname : pathname.replace(marketPattern, '')

  return pathnameWithoutMarket.startsWith('/cart')
}

class PushCartError {
  description = ''

  status = null

  MESSAGES = {
    ADD_TOO_MANY: `Unfortunately, there isn't enough of this product available`,
    ALREADY_HAVE_ALL_STOCK: `You already have all of the remaining stock in your cart`,
    CANNOT_FIND_VARIANT: 'There was an error adding this product to cart.',
    GENERIC: 'There was an error adding this product to cart.',
    NOT_ENOUGH_IN_STOCK: `Unfortunately, there isn't enough of this product available`,
    SOLD_OUT: `Unfortunately, this product is sold out.`,
  }

  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.description = error.description
    this.status = error.status

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone = this.pushCart.els['error-template'].element.content.cloneNode(
      true,
    )
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description?.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description?.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors?.toLowerCase() === 'bag error'
  }

  get soldOut () {
    return this.description?.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description?.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
  }

  constructor () {
    super()

    this.hasBeenInteractedWith = false

    this.sections = window.BAO.sectionRenderer.pushCartSections

    this.checkOverflow()

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
    this.onAfterUpdateRemoveItem = this.onAfterUpdateRemoveItem.bind(this)
    this.checkOverflow = this.checkOverflow.bind(this)
    this.onDrawerTrigger = this.onDrawerTrigger.bind(this)

    this.addEventListener('click', () => {
      this.hasBeenInteractedWith = true
    })

    this.addEventListener('focusin', () => {
      this.hasBeenInteractedWith = true
    })

    this.els.body.element?.addEventListener('scroll', () => {
      this.hasBeenInteractedWith = true
    })

    this.listeners.add(window, 'resize', debounce(this.checkOverflow, 50))

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.onAfterUpdateRemoveItem,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.DRAWER.TRIGGER,
      this.onDrawerTrigger,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onBeforeCartAdd,
    )
  }

  onBeforeCartAdd () {
    if (!isCartPage()) {
      this.drawers.activeDrawerKey = this.drawer.getAttribute('key')
    }

    this.loading = true
  }

  onAfterCartAdd (event) {
    this.hasBeenInteractedWith = false

    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    this.loading = false

    this.checkOverflow()

    // Track cart add in Ometria
    this.trackOmetriaCartAdd(event)

    setTimeout(() => {
      if (this.hasBeenInteractedWith) return
      window.BAO.drawers.closeDrawer()
    }, 3000)
  }

  onAfterUpdateRemoveItem () {
    setTimeout(() => {
      this.checkOverflow()

      // Track cart update/remove in Ometria
      this.trackOmetriaCartUpdate()
    }, 100)
  }

  async trackOmetriaCartAdd (event) {
    try {
      if (!window.ometria) return

      // Update the basket with current cart state
      await this.updateOmetriaBasket()

      // Track the specific add action if we have the added item info.
      if (event.detail.items && event.detail.items.length > 0) {
        const addedItem = event.detail.items[0]

        window.ometria.trackAddToBasket(
          addedItem.id.toString(),
          addedItem.quantity,
        )
      }
    } catch (error) {
      console.error('Ometria cart add tracking error:', error)
    }
  }

  async trackOmetriaCartUpdate () {
    try {
      if (!window.ometria) return

      // Update the basket with current cart state
      await this.updateOmetriaBasket()
    } catch (error) {
      console.error('Ometria cart update tracking error:', error)
    }
  }

  async updateOmetriaBasket () {
    const cartResponse = await fetch('/cart.js')
    const cartData = await cartResponse.json()
    const basketId = cartData.token

    const basket = new window.ometria.Basket()

    basket.setId(basketId)
    basket.setTotal(cartData.total_price / 100, cartData.currency)

    cartData.items.forEach(line => {
      const productId = line.product_id.toString()
      const quantity = line.quantity
      const price = line.final_line_price / 100
      const variantId = line.variant_id.toString()

      basket.addLineItem(productId, quantity, price, variantId)
    })

    window.ometria.setBasket(basket)
  }

  onDrawerTrigger (e) {
    if (e.detail.key !== 'cart') return

    this.checkOverflow()
  }

  checkOverflow () {
    const scrollbarWidth =
      this.els.body.element.offsetWidth - this.els.body.element.clientWidth

    if (scrollbarWidth > 0) {
      this.els.body.element.style.setProperty(
        '--ScrollbarWidth',
        `${scrollbarWidth}px`,
      )
    } else {
      this.els.body.element.style.setProperty('--ScrollbarWidth', '0px')
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawers () {
    return this.closest('site-drawers')
  }

  get drawer () {
    return this.closest('site-drawer')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
